import {
  useAvailableYears,
  useYearlyPerformance,
} from '@/entities/performance/hooks/usePerformanceQueries';
import {
  EmptyState,
  ErrorState,
  LoadingBlock,
} from '@/shared/components/StateViews';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { toUrlYm } from '@/shared/utils/format';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import {
  AnnualKpis,
  AnnualTrendCard,
  MonthCardGrid,
} from './components/AnnualSections';
import { ExcelExportButton } from './components/PerformanceParts';

/**
 * M-08 실적 대시보드 (연간)
 * API: GET /web/performance/available-years, /yearly?year=
 * 연간 합계 · 정산 비율 · 월 카드 상태는 서버 값. 목표 · 전년 비교 · 기준일 · 엑셀은 아직 없다
 * (docs/api/README.md B1 · B2 · B4) — 기준일은 그리지 않고, 엑셀은 비활성
 */
export const PerformancePage = () => {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { year?: number };
  const yearsQuery = useAvailableYears();
  const years = useMemo(
    () => [...(yearsQuery.data ?? [])].sort((a, b) => b - a),
    [yearsQuery.data],
  );
  const year =
    search.year && years.includes(search.year) ? search.year : years[0];

  const yearlyQuery = useYearlyPerformance(year);
  const rows = yearlyQuery.data?.months ?? [];

  const goMonth = (ym: string) =>
    void navigate({
      to: ROUTE_PATHS.PERFORMANCE_MONTH,
      params: { yearMonth: toUrlYm(ym) },
    });

  const header = (
    <div className="flex items-center justify-between gap-3 lg:h-12">
      <div className="flex items-center gap-3.5">
        <h1 className="text-2xl leading-9 font-bold text-ink lg:text-[28px] lg:leading-[42px]">
          실적
        </h1>
        {year && (
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-2.5 rounded-md border border-[#dbdbdb] bg-surface py-[9px] pr-3.5 pl-4 outline-none">
              <span className="text-sm leading-[21px] font-bold text-ink">
                {year}년
              </span>
              <span className="text-xs leading-[18px] text-[#767676]">▾</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {years.map((y) => (
                <DropdownMenuItem
                  key={y}
                  onSelect={() =>
                    void navigate({
                      to: ROUTE_PATHS.PERFORMANCE,
                      search: { year: y },
                    })
                  }
                >
                  {y}년
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {/* TODO(server): 기준일 필드가 없어 "기준일 YYYY.MM.DD"를 그리지 않는다 (docs/api B4) */}
      </div>
      <ExcelExportButton />
    </div>
  );

  if (yearsQuery.isError || yearlyQuery.isError) {
    return (
      <div className="flex flex-col gap-5">
        {header}
        <ErrorState
          onRetry={() => {
            void yearsQuery.refetch();
            void yearlyQuery.refetch();
          }}
        />
      </div>
    );
  }

  if (yearsQuery.isSuccess && years.length === 0) {
    return (
      <div className="flex flex-col gap-5">
        {header}
        <EmptyState message="아직 실적이 없어요. 처방을 등록하면 이곳에 실적이 쌓여요." />
      </div>
    );
  }

  const loading = yearsQuery.isPending || yearlyQuery.isPending;

  return (
    <div className="flex flex-col">
      {header}
      <div className="mt-[18px]">
        {loading || !yearlyQuery.data ? (
          <LoadingBlock className="h-[108px]" />
        ) : (
          <AnnualKpis yearly={yearlyQuery.data} />
        )}
      </div>
      <div className="mt-5">
        {loading || !year ? (
          <LoadingBlock className="h-[340px]" />
        ) : (
          <AnnualTrendCard year={year} rows={rows} onSelectMonth={goMonth} />
        )}
      </div>
      <h2 className="mt-6 mb-3 text-lg leading-[27px] font-bold text-ink">
        월별 실적
      </h2>
      {loading || !year ? (
        <LoadingBlock className="h-[420px]" />
      ) : (
        <MonthCardGrid year={year} rows={rows} onSelectMonth={goMonth} />
      )}
    </div>
  );
};
